import {createTheme} from "@mui/material/styles";

const white = '#ffffff';

const secondary = {
    light: '#343434',
    main: '#1b2127',
    dark: '#0b161d',
};

export const theme = createTheme({
    typography: {
        fontFamily: 'var(--body_typography-font-family)',
        h2: {
            fontSize: '2rem',
            lineHeight: '2.3rem',
            fontFamily: 'var(--h2_typography-font-family)',
            fontWeight: 'var(--h2_typography-font-weight)',
        },
        h3: {
            fontSize: '1.5rem',
            lineHeight: '1.8rem',
            fontFamily: 'var(--h3_typography-font-family)',
            fontWeight: 'var(--h3_typography-font-weight)',
        },
    },
    palette: {
        primary: {
            light: '#f5f5f5',
            main: '#fbe74c',
            dark: '#343434',
            contrastText: '#ffffff',
        },
        secondary: {
            light: '#343434',
            main: '#1b2127',
            dark: '#0b161d',
            contrastText: '#ffffff',
        },
        background: {
            default: '#ebedf4'
        },
    },
    breakpoints: {
        values: {
            xs: 0,
            sm: 768,
            md: 960,
            lg: 1280,
            xl: 1920
        }
    },
    components: {
        MuiDialog: {
            styleOverrides: {
                paper: {
                    borderRadius: '10px',
                    padding: '20px',
                }
            }
        },
        MuiDialogTitle: {
            styleOverrides: {
                root: {
                    fontSize: '1.8rem',
                    lineHeight: '2.1rem',
                    fontFamily: 'var(--h3_typography-font-family)',
                    fontWeight: 'var(--h3_typography-font-weight)',
                }
            }
        },
        MuiTableCell: {
            styleOverrides: {
                root: {
                    borderBottom: "1px solid transparent",
                },
                head: {
                    color: '#ffffff',
                    backgroundColor: '#2f4d88',
                    border: '1px solid transparent',
                    fontSize: '1em',
                },
                body: {
                    border: '1px solid transparent',
                    transition: 'all 0.3s ease',
                },
            }
        },
        MuiTableRow: {
            styleOverrides: {
                root: {
                    '&:nth-of-type(even)': {
                        opacity: 0.88,
                    },
                    '&:hover': {
                        '& .MuiTableCell-root': {
                            borderBottom: '1px solid #55555536',
                            borderTop: '1px solid #55555536',
                        },
                    }
                }
            }
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    boxShadow: '0px 2px 4px -1px rgb(0 0 0 / 9%)',
                },
                colorDefault: {
                    backgroundColor: white,
                    boxShadow: 'unset !important',
                },
                colorPrimary: {
                    backgroundColor: white,
                    boxShadow: 'unset !important',
                }
            }
        },
        MuiGrid: {
            styleOverrides: {
                root: {},
                item: {},
                container: {
                    padding: '3px',
                }
            }
        },
        MuiTabs: {
            styleOverrides: {
                root: {
                    borderBottom: '1px solid',
                    borderBottomColor: secondary.dark,
                },
                indicator: {
                    display: "flex",
                    justifyContent: "center",
                    backgroundColor: "transparent",
                    'span': {
                        maxWidth: 1,
                        minHeight: 12,
                        maxHeight: 12,
                        backgroundColor: secondary.dark,
                        position: "absolute",
                        bottom: 0,
                        width: 1
                    }
                },
            }
        },
        MuiTab: {
            styleOverrides: {
                root: {
                    fontSize: "14px",
                    fontWeight: "bold",
                    color: "#6f7a81",
                    maxWidth: "390px",
                    '&:hover': {
                        opacity: 1,
                    }
                },
            }
        },
        MuiOutlinedInput: {
            styleOverrides: {
                root: {
                    borderRadius: '5px',

                    //style input element with no border
                    '& input': {
                        border: 'none !important',
                    }
                },

                //style input element with no border
                input: {
                    border: 'none !important',
                }
            }
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    boxShadow: 'none',
                    backgroundColor: 'transparent',
                    color: 'var(--button_accent_color);',
                    '&:hover': {
                        boxShadow: 'none',
                    }
                },
                contained: {
                    background: 'var(--button_gradient_top_color);',
                    '--button_gradient': 'linear-gradient(var(--button_gradient_angle, 180deg), var(--button_gradient_top_color) var(--button_gradient_start, 0%), var(--button_gradient_bottom_color) var(--button_gradient_end, 100%));',
                    fontWeight: 'var(--button_typography-font-weight)',
                    fontFamily: 'var(--button_typography-font-family);',
                    fontSize: 'var(--button_font_size, 14px)',
                    lineHeight: 'var(--button_line_height, 1)',
                    letterSpacing: 'var(--button_typography-letter-spacing)',
                    borderRadius: 'var(--button-border-radius-top-left,0) var(--button-border-radius-top-right,0) var(--button-border-radius-bottom-right,0) var(--button-border-radius-bottom-left,0)',
                    paddingTop: 'var(--button_padding-top, 13px)',
                    paddingRight: 'var(--button_padding-right, 29px)',
                    paddingBottom: 'var(--button_padding-bottom, 13px)',
                    paddingLeft: 'var(--button_padding-left, 29px)',
                    color: 'var(--awb-color4)',
                    backgroundColor: 'var(--button_accent_color);',
                    backgroundImage: 'none',
                    '&:disabled': {
                        backgroundColor: 'var(--button_accent_color);',
                    }
                },
                outlined: {
                    '&:hover': {
                        backgroundColor: 'transparent',
                    }
                }
            }
        },
        MuiInput: {
            styleOverrides: {
                root: {
                    borderRadius: '5px',
                    '&:before': {
                        borderBottom: '1px solid #262527',
                    },
                    '&:after': {
                        borderBottom: 'none',
                    }
                },
            }
        },
        MuiInputBase: {
            styleOverrides: {
                root: {
                    backgroundColor: 'transparent',
                    width: '100%',
                    border: 0,
                    borderBottom: '1px solid #262527',
                    borderRadius: '0 !important',
                },
                input: {
                    backgroundColor: 'transparent !important',
                },
            }
        },
        MuiSelect: {
            styleOverrides: {
                root: {
                    borderRadius: 0,
                }
            }
        },

        MuiLinearProgress: {
            styleOverrides: {
                bar: {
                    backgroundColor: 'primary.main',
                }
            }
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: 'black',
                }
            }
        },
        MuiInputAdornment: {
            styleOverrides: {
                root: {
                    color: '#262527',
                    width: '.75em',
                    '& .MuiSvgIcon-root': {
                        width: '.75em',
                    },
                    '& .MuiTypography-root': {
                        margin: 0,
                    }
                }
            },
        }
    },
});
